import clsx from 'clsx';
import React, { RefObject, useEffect } from 'react';
import { EditorModes, FormatterModes } from '../../../../code/interfaces/editor';
import { DESTINATION_TYPES } from '../../../../containers/destination/constants/destinationTypes';
import { EntityUIStateWrapper } from '../../../../core/interfaces/entitiy-ui-state';
import { RbacPermissions } from '../../../../core/interfaces/user';
import { SCHEMA_EXPLORER_DEFAULT_UI_OPTIONS } from '../../../schema-explorer/constants/schemaExplorer';
import { SchemaExplorerUIOptions } from '../../../schema-explorer/interfaces/schemaExplorer';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';
import { ElapseTimerRef } from '../../../../date-time/components/elapse-timer/ElaspeTimer';
import { HdCoordinatedHeight, useCoordinatedHeightElement } from '../../../../layout/components/coordinated-height/HdCoordinatedHeight';
import { SchemaBuilderContainer } from '../../../schema-builder/components/SchemaBuilderContainer';
import { SchemaBuilderControllerProps } from '../../../schema-builder/interfaces/SchemaBuilderController';
import SchemaExplorer from '../../../schema-explorer/components/schema-explorer/SchemaExplorer';
import { ScrollToViewportOnLoad } from '../../../../a11y/scroll/components/scroll-to-viewport-on-load/ScrollToViewportOnLoad';
import { HdPane } from '../../../../components';
import HdIndeterminateProgressBar from '../../../../progress-indicators/components/indeterminate-progress-bar/HdIndeterminateProgressBar';
import { QueryExecutionResponse, WorkbenchQueryEditorConfiguration } from '../../interfaces/workbench';
import styles from './Workbench.module.scss';
import { useWorkbench } from '../../hooks/useWorkbench';
import { WorkbenchQueryEditorContainer } from '../workbench-query-editor-container/WorkbenchQueryEditorContainer';
import { WorkbenchQueryResultContainer } from '../workbench-query-result-container/WorkbenchQueryResultContainer';
import { WorkbenchQueryViewShimmer } from '../workbench-query-view-shimmer/WorkbenchQueryViewShimmer';

export interface WorkbenchProps {
  query: string;
  queryError?: any;
  onQueryChange: (query: string) => any;
  queryExecutionResponse: QueryExecutionResponse;
  onQueryExecutionResponseChange: (res: QueryExecutionResponse) => any;
  destinationId: number;
  dataSource;
  dataSourceUIState: EntityUIStateWrapper;
  getDataSource: () => void;
  onFolderDelete: (folderName: string) => void;
  schemaExplorerWrapperClassName?: string;
  schemaExplorerUIOptions?: SchemaExplorerUIOptions;
  showingInsideCard?: boolean;
  canShowViews?: boolean;
  className?: string;
  Header?: React.ElementType;
  queryOutputInitialData?: any;
  onQueryStateUpdate?: (stateUpdated: boolean) => {};
  onQueryValidationChange?: (valid: boolean) => void;
  queryEditorProps?: WorkbenchQueryEditorConfiguration;
  elapsedTimerRef?: RefObject<ElapseTimerRef>;
  elapsedTimerInitialValue?: number;
  stopQueryExecution$: (taskId: number) => Promise<any>;
  getQueryExecutionTaskId$: (sourceId: number, query: string) => Promise<any>;
  getQueryExecutionTaskResponse$: (taskId: number, query?: string) => Promise<any>;
  duplicateColErrorMessage?: string;
  rbacPermission?: RbacPermissions;
  runQueryShortcutCommand: string;
  onRunQueryClick?: Function;
}

export function Workbench({
  query,
  queryError,
  queryExecutionResponse,
  onQueryChange,
  onQueryExecutionResponseChange,
  destinationId,
  dataSource,
  dataSourceUIState,
  getDataSource,
  onFolderDelete,
  schemaExplorerWrapperClassName = '',
  schemaExplorerUIOptions = SCHEMA_EXPLORER_DEFAULT_UI_OPTIONS,
  showingInsideCard,
  canShowViews,
  className = '',
  Header,
  elapsedTimerRef,
  elapsedTimerInitialValue,
  queryEditorProps,
  stopQueryExecution$,
  getQueryExecutionTaskId$,
  getQueryExecutionTaskResponse$,
  duplicateColErrorMessage = '',
  rbacPermission,
  runQueryShortcutCommand,
  onRunQueryClick
}: WorkbenchProps) {
  const [ schemaExplorerHeightElem, setSchemaExplorerHeightElem ] = useCoordinatedHeightElement();

  const onExecutionStart = () => {
    elapsedTimerRef?.current?.reset();
  };

  const onExecutionEnd = () => {
    elapsedTimerRef?.current?.stop();
  };

  const {
    execute,
    stop,
    queryRunErrorMessage,
    hasDuplicateColumns,
    runningQuery,
    queryExecutionStatus,
    noResultOp,
    lastRunActionTime,
    hasMoreResults
  } = useWorkbench({
    sourceId: destinationId,
    query,
    onExecutionStart,
    onExecutionEnd,
    queryExecutionResponse,
    onQueryExecutionResponseChange,
    stopQueryExecution$,
    getQueryExecutionTaskId$,
    getQueryExecutionTaskResponse$
  });

  const queryOutput =  queryExecutionResponse?.data?.output && Object.keys(queryExecutionResponse.data.output).length ?
    queryExecutionResponse.data.output : undefined;

  const dataSourceTypeRef = dataSource ? DESTINATION_TYPES[dataSource.dest_type] : null;

  const runQuery = () => {
    execute();
  };

  const dataIdGenerator = getDataIdGenerator('workbench');

  return (
    <SchemaBuilderContainer
      className={`d-flex align-items-start ${className}`}
    >
      {(schemaBuilderController: SchemaBuilderControllerProps) => (<>
        <HdCoordinatedHeight elRef={schemaExplorerHeightElem} fixedHeight>
          <div
            className={clsx(styles.workbenchSchemaExplorerWrapper, schemaExplorerWrapperClassName)}
            ref={setSchemaExplorerHeightElem}
          >
            <SchemaExplorer
              showingInsideCard={showingInsideCard}
              source={dataSource}
              uiState={dataSourceUIState}
              canShowViews={canShowViews}
              schemaExplorerUIOptions={schemaExplorerUIOptions}
              getSource={getDataSource}
              onFolderDelete={onFolderDelete}
              onCreateTableClick={() => schemaBuilderController.show({
                destinationId,
                dataTypes: [],
                options: {
                  destType: dataSource.dest_type,
                  removeNestedDataTypes: true
                },
                close: (data) => {
                  if (!!data) {
                    getDataSource();
                  }
                }
              })}
            />
          </div>
        </HdCoordinatedHeight>

        <div className={clsx(styles.queryViewContainer, showingInsideCard ? 'pl-0' : '')}>
          {!!Header ? (
            <Header dataId={dataIdGenerator('header')} />
          ) : null}

          {!dataSource ? (
            <WorkbenchQueryViewShimmer showingInsideCard={showingInsideCard} />
          ) : (
            <WorkbenchQueryEditorContainer
              query={query}
              queryError={queryError}
              onQueryChange={onQueryChange}
              runningQuery={runningQuery}
              queryExecutionStatus={queryExecutionStatus}
              lastRunActionTime={lastRunActionTime}
              queryOutput={queryOutput}
              showingInsideCard={showingInsideCard}
              runQuery={runQuery}
              onRunQueryClick={onRunQueryClick}
              stopRunningQuery={stop}
              queryEditorProps={queryEditorProps}
              elapsedTimerRef={elapsedTimerRef}
              elapsedTimerInitialValue={elapsedTimerInitialValue}
              sqlEditorMode={dataSourceTypeRef?.editorMode || EditorModes.SQL}
              sqlFormatterMode={dataSourceTypeRef?.formatterMode || FormatterModes.SQL}
              formatterEnabled={!dataSourceTypeRef?.queryFormattingDisabled}
              rbacPermission={rbacPermission}
              runQueryShortcutCommand={runQueryShortcutCommand}
            />
          )}

          {runningQuery ? (
            <ScrollToViewportOnLoad>
              <HdIndeterminateProgressBar color='grey' className={styles.queryRunOutputLoading}>
                Hevo is running your query
              </HdIndeterminateProgressBar>
            </ScrollToViewportOnLoad>
          ) : null}

          {!!queryRunErrorMessage || (hasDuplicateColumns && duplicateColErrorMessage) ? (
            <HdPane
              dataId={dataIdGenerator('')}
              variant='error-faded'
              className='surface-box mt-5'
              icon='error-filled'
            >
              <div className='text-medium'>
                {queryRunErrorMessage || duplicateColErrorMessage}
              </div>
            </HdPane>
          ) : null}

          {!runningQuery ? (
            <WorkbenchQueryResultContainer
              className={ clsx(!showingInsideCard ? 'mt-5' : '') }
              queryOutput={queryOutput}
              noResultOp={noResultOp}
              hasMoreResults={hasMoreResults}
              showingInsideCard={showingInsideCard}
            />
          ) : null}
        </div>
      </>)}
    </SchemaBuilderContainer>
  );
}
